import {
	ContactDetailsModel,
	ContactSource,
	getGlobalContactDetails,
	getPersonalContactDetails,
	getWorkspaceContactDetails
} from "client/Api/Email/contacts";
import { AddressBookContactSource } from "../Overview/Overview";
import { useEffect, useState } from "react";
import {
	AlertManager,
	useAlerts,
	useForceRerender
} from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { isNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { HttpRequestError } from "client/Api/httpClient";

export type ContactDetailsLoadingErrorKind = "unknown" | "not-found";

export type ContactDetailsLoadingState = {
	kind: "loading";
} | {
	kind: "loaded";
	contact: ContactDetailsModel;
} | {
	kind: "error";
	errorKind: ContactDetailsLoadingErrorKind;
};

export type FetchContactDetailsHandlers<TState extends ContactDetailsLoadingState | undefined> = {
	state: TState;
	reload(): void;
};

export function useFetchContactDetails(
	source: AddressBookContactSource,
	contactId: string
): FetchContactDetailsHandlers<ContactDetailsLoadingState>;
export function useFetchContactDetails(
	source: AddressBookContactSource,
	contactId?: string
): FetchContactDetailsHandlers<ContactDetailsLoadingState | undefined>;
export function useFetchContactDetails(
	source: AddressBookContactSource,
	contactId: string | undefined
): FetchContactDetailsHandlers<ContactDetailsLoadingState | undefined> {
	const [state, setState] = useState<ContactDetailsLoadingState | undefined>();
	const alertManager: AlertManager = useAlerts();
	const [reload, dataVersion] = useForceRerender();

	useEffect(
		() => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchContact(contactId: string, signal: AbortSignal): Promise<void> {
				try {
					setState({ kind: "loading" });
					let loadedContact: ContactDetailsModel;

					switch(source) {
						case ContactSource.Personal:
							loadedContact = await getPersonalContactDetails(contactId, signal);
							break;
						case ContactSource.Gal:
							loadedContact = await getGlobalContactDetails(contactId, signal);
							break;
						case ContactSource.Workspace:
							loadedContact = await getWorkspaceContactDetails(contactId, signal);
							break;
						default:
							never(source);
					}

					setState({
						kind: "loaded",
						contact: loadedContact
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					const errorKind: ContactDetailsLoadingErrorKind = e instanceof HttpRequestError
						&& e.statusCode === 404
							? "not-found"
							: "unknown";

					if (errorKind === "unknown") {
						alertManager.showError(localize("_GeneralErrorMessage"));
					}

					setState({
						kind: "error",
						errorKind: errorKind
					});
				}
			}

			if (isNil(contactId)) {
				setState(undefined);
				return undefined;
			}

			const cancellation = new AbortController();
			fetchContact(contactId, cancellation.signal);

			return () => cancellation.abort();
		},
		[
			contactId,
			source,
			alertManager,
			dataVersion
		]
	);

	return {
		state: isNil(contactId)
			? undefined
			: state ?? { kind: "loading" },
		reload
	};
}